import { Link } from "react-router-dom";
import { useT } from "../i18n/I18nProvider";
import {
  ContentSection,
  AutoTOC,
  KeyPointsBlock,
  PL,
  Sub,
} from "../components/contentTemplate";
import { ACHETER_BIEN_FAQ } from "../data/acheterBienFaq";

const SECTIONS = [
  { id: "cadre-legal",     title: "Le cadre légal" },
  { id: "ue-eee-suisse",   title: "Ressortissant UE, EEE ou suisse" },
  { id: "pays-tiers",      title: "Ressortissant d'un pays tiers" },
  { id: "autorisation",    title: "L'autorisation du ministère de l'Intérieur" },
  { id: "frais-taxes",     title: "Frais et taxes" },
  { id: "faq",             title: "Questions fréquentes" },
];

// Shared classes
const EXT = "text-[#0056D2] hover:underline";
const EXT_PROPS = { target: "_blank", rel: "noopener noreferrer" };
const INTERNAL_LINK = "text-[#0056D2] hover:underline";

export default function AcheterBienImmobilier() {
  const { lp } = useT();

  return (
    <article
      className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16"
      data-testid="acheter-bien-immobilier-page"
    >
      {/* Hero */}
      <header className="mb-10" data-testid="page-hero">
        <span className="hej-chip" data-testid="page-chip">
          <span className="w-1.5 h-1.5 rounded-full bg-[#DC143C]"></span>
          Mis à jour en juin 2026
        </span>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-4">
          Puis-je acheter un appartement ou une maison en Pologne ?
        </h1>
      </header>

      {/* Introduction (sans titre intermédiaire) */}
      <section
        className="space-y-4 text-slate-700 leading-relaxed text-lg mb-12"
        data-testid="page-lede"
      >
        <p>
          La réponse dépend de votre nationalité, puis de la nature et de la localisation du bien. Si vous êtes ressortissant suisse,
          d'un pays de l'Union européenne ou de l'Espace économique européen (Islande, Liechtenstein, Norvège), vous achetez un
          appartement, une maison ou un terrain dans les mêmes conditions qu'un citoyen polonais, sans aucune autorisation, partout
          sur le territoire, y compris au bord de la Baltique. Si vous êtes ressortissant d'un pays tiers, l'achat reste possible,
          mais une autorisation du ministère de l'Intérieur est exigée dans certains cas : la règle est souple pour un appartement,
          plus stricte pour une maison, et elle se durcit pour tous les biens en zone frontalière.
        </p>
        <p>
          Cette page traite de l'éligibilité à l'achat et de ses coûts. Elle ne traite pas du droit de séjour ni de la résidence
          fiscale, qui sont des sujets distincts : posséder un bien en Pologne ne donne aucun droit de séjour et ne détermine pas à
          lui seul votre résidence fiscale. Pour le séjour, voyez la rubrique{" "}
          <Link to={lp("/demarches")} className={INTERNAL_LINK}>Démarches</Link> et, pour les ressortissants de pays tiers, la page{" "}
          <Link to={lp("/demarches-pays-tiers")} className={INTERNAL_LINK}>
            Démarches pour les ressortissants de pays tiers
          </Link>.
        </p>
      </section>

      {/* Key points */}
      <KeyPointsBlock testid="page-key-points">
        <ul className="list-disc pl-5 space-y-2">
          <li>
            Suisses et ressortissants UE/EEE : dispense totale et sans condition, partout (zone frontalière comprise) et pour tout
            bien (terres agricoles comprises).
          </li>
          <li>
            Tout autre étranger peut acheter un appartement autonome sans autorisation, à condition qu'il ne soit pas situé en zone
            frontalière.
          </li>
          <li>
            Pour un ressortissant de pays tiers, une maison avec terrain exige en principe une autorisation du ministère de
            l'Intérieur.
          </li>
          <li>
            Toute la côte balte est en zone frontalière, Tricité comprise : un ressortissant de pays tiers y a besoin d'une
            autorisation même pour un appartement.
          </li>
          <li>
            L'autorisation coûte 1 570 zł, reste valable deux ans, et s'obtient en deux à quatre mois en pratique.
          </li>
          <li>Les taxes d'achat sont les mêmes pour tous : 2 % dans l'ancien, TVA déjà incluse dans le neuf.</li>
        </ul>
      </KeyPointsBlock>

      {/* Sommaire */}
      <AutoTOC sections={SECTIONS} testid="page-toc" />

      {/* Sections */}
      <ContentSection id="cadre-legal" n={1} title="Acheter un bien immobilier en Pologne : le cadre légal">
        <p>
          L'acquisition immobilière par les étrangers est régie par la loi du 24 mars 1920 sur l'acquisition de biens immobiliers
          par les étrangers (
          <a href="https://isap.sejm.gov.pl/isap.nsf/DocDetails.xsp?id=WDU19200310178" className={EXT} {...EXT_PROPS}>
            texte consolidé sur ISAP
          </a>
          ), l'un des plus anciens textes encore en vigueur en Pologne, modifié à de nombreuses reprises depuis l'adhésion à l'Union
          européenne.
        </p>
        <p>
          Le principe est qu'un étranger (<PL>cudzoziemiec</PL>) doit obtenir une autorisation du ministre compétent pour les
          affaires intérieures, le MSWiA, avant d'acquérir la propriété ou l'usufruit perpétuel d'un bien, comme l'expose le portail
          officiel (
          <a
            href="https://www.gov.pl/web/mswia/uzyskaj-zezwolenie-na-nabycie-nieruchomosci-akcji-udzialow-przez-cudzoziemcow"
            className={EXT}
            {...EXT_PROPS}
          >
            gov.pl
          </a>
          ). Une acquisition réalisée sans l'autorisation requise est juridiquement nulle.
        </p>
        <p>
          L'essentiel tient dans les dérogations de l'article 8, qui se lisent à deux niveaux indépendants : votre nationalité
          d'abord, la nature et la localisation du bien ensuite. Ces dérogations sont distinctes et ne se cumulent pas, ce qui a
          une conséquence pratique importante détaillée plus bas.
        </p>
      </ContentSection>

      <ContentSection id="ue-eee-suisse" n={2} title="Acheter en Pologne quand on est ressortissant UE, EEE ou suisse">
        <p>
          Si vous êtes ressortissant ou entreprise de l'Union européenne, de l'Espace économique européen ou de la Suisse, vous
          êtes dispensé de l'autorisation du ministère, quelle que soit l'origine du bien, comme le confirme le guide officiel (
          <a href="https://www.biznes.gov.pl/pl/portal/ou209" className={EXT} {...EXT_PROPS}>biznes.gov.pl</a>
          ). Sur le plan administratif polonais, le ressortissant suisse est traité à l'identique d'un ressortissant de l'Union. La
          dispense suit la nationalité, quel que soit le lieu d'origine ou de résidence.
        </p>
        <p>
          Cette dispense découle de l'alinéa 2 de l'article 8, distinct des dispenses générales de l'alinéa 1. La distinction n'est
          pas qu'une question de référence : elle rend la dispense autonome et inconditionnelle. La restriction propre à la zone
          frontalière, qui touche les autres étrangers, se rattache aux dispenses de l'alinéa 1 et ne s'applique donc pas à vous.
          Un ressortissant UE ou suisse achète un appartement ou une maison même dans une commune côtière de la Baltique sans
          solliciter d'autorisation, et l'idée d'attacher une condition « hors zone frontalière » à ce cas serait une lecture
          erronée.
        </p>
        <p>
          Cette liberté vaut pour tous les types de biens, terres agricoles et forestières comprises. Une période transitoire,
          négociée lors de l'adhésion, avait maintenu l'obligation d'autorisation pour ces terrains pendant douze ans. Elle s'est
          éteinte le 1er mai 2016, et aucune autorisation n'est plus requise depuis.
        </p>
        <p>
          Une nuance subsiste pour les terres agricoles, mais elle ne tient pas à la nationalité. La loi sur la structure agraire
          (<PL>ustawa o kształtowaniu ustroju rolnego</PL>) encadre l'achat de terres agricoles pour tout le monde, citoyens
          polonais compris : droit de préemption de l'agence publique KOWR au-delà d'un hectare, conditions liées au statut
          d'exploitant pour les grandes surfaces, exemption des parcelles inférieures à 0,3 hectare. Un ressortissant UE ou suisse
          a donc les mêmes droits et les mêmes contraintes qu'un Polonais. Pour un appartement ou une maison d'habitation, ces
          règles agricoles ne s'appliquent pas.
        </p>
        <p>
          Acheter ne suppose pas de résider en Pologne. Si vous vous installez, l'enregistrement de votre séjour relève d'une
          procédure distincte, détaillée dans la rubrique{" "}
          <Link to={lp("/demarches")} className={INTERNAL_LINK}>Démarches</Link>.
        </p>
      </ContentSection>

      <ContentSection id="pays-tiers" n={3} title="Acheter en Pologne quand on est ressortissant d'un pays tiers">
        <p>
          Pour un ressortissant d'un pays hors UE, EEE et Suisse, la règle générale est celle de l'autorisation. Deux éléments la
          nuancent fortement : une dérogation pour les appartements, et une limite qui la neutralise en zone frontalière.
        </p>
        <p>
          La loi dispense d'autorisation l'achat d'un logement autonome (<PL>samodzielny lokal mieszkalny</PL> au sens de la loi du
          24 juin 1994 sur la propriété des locaux), indépendamment de la nationalité et sans aucune condition de résidence ou de
          titre de séjour. Un ressortissant de pays tiers peut donc acheter un appartement dans un immeuble collectif sans
          autorisation, au même titre qu'un citoyen polonais, comme le confirme le portail (
          <a href="https://www.biznes.gov.pl/pl/portal/ou209" className={EXT} {...EXT_PROPS}>biznes.gov.pl</a>
          ). Concrètement, un acheteur chinois ou colombien acquiert librement un appartement à Varsovie, Cracovie, Wrocław, Poznań
          ou Łódź. La dispense couvre aussi le local ou la quote-part de local servant de garage rattaché au logement, ainsi que la
          quote-part du terrain sur lequel l'immeuble est bâti.
        </p>
        <p>
          La différence se joue sur la nature du bien. Un appartement en copropriété ne demande pas d'autorisation. Une maison
          individuelle, un terrain à bâtir, une parcelle nue ou une terre agricole, indissociables du sol, constituent des biens
          fonciers qui exigent en principe une autorisation pour un ressortissant de pays tiers.
        </p>
        <p>
          Trois autres situations dispensent un ressortissant de pays tiers de l'autorisation pour un bien foncier : résider en
          Pologne depuis au moins cinq ans sous un titre de séjour permanent ou de résident de longue durée UE, être marié à un
          citoyen polonais et résider en Pologne depuis au moins deux ans sous titre de séjour lorsque le bien entre dans la
          communauté de biens, ou hériter par succession légale.
        </p>
        <p>
          Ces dispenses, appartement compris, connaissent une limite décisive. L'alinéa 3 de l'article 8 prévoit qu'aucune d'elles
          ne s'applique aux biens situés en zone frontalière (<PL>strefa nadgraniczna</PL>), ni aux terres agricoles de plus d'un
          hectare (
          <a
            href="https://sip.lex.pl/akty-prawne/dzu-dziennik-ustaw/nabywanie-nieruchomosci-przez-cudzoziemcow-16777234/art-8"
            className={EXT}
            {...EXT_PROPS}
          >
            texte de l'article 8
          </a>
          ). Pour un bien situé en zone frontalière, un ressortissant de pays tiers a donc besoin d'une autorisation, que ce soit
          un appartement ou une maison.
        </p>

        <Sub>Zone frontalière : l'exception qui couvre tout le littoral</Sub>
        <p>
          La portée de cette zone surprend, et elle est plus large qu'on ne l'imagine. Sa définition figure à l'article 12 de la
          loi du 12 octobre 1990 sur la protection de la frontière de l'État (
          <a href="https://isap.sejm.gov.pl/isap.nsf/DocDetails.xsp?id=WDU19900780461" className={EXT} {...EXT_PROPS}>
            texte sur ISAP
          </a>
          ). Ce n'est pas une bande mesurée en mètres, mais une notion administrative définie par communes entières : dès qu'une
          commune touche la frontière terrestre ou, côté mer, le rivage, c'est l'intégralité de son territoire qui se trouve en
          zone frontalière. Si la largeur ainsi obtenue n'atteint pas quinze kilomètres, les communes directement voisines y sont
          aussi rattachées.
        </p>
        <p>
          La conséquence est nette pour l'immobilier côtier : toute la côte balte est en zone frontalière. Sont concernées la
          Tricité, soit Gdańsk, Sopot et Gdynia, mais aussi Kołobrzeg, Świnoujście, Międzyzdroje, Ustka, Łeba, Władysławowo, Hel et
          Krynica Morska. Les communes longeant les frontières terrestres relèvent du même régime, à l'est comme au sud et à
          l'ouest. Un ressortissant de pays tiers ne peut donc pas acheter d'appartement sans autorisation dans les grandes villes
          balnéaires, alors qu'il le fait librement dans les grandes villes de l'intérieur citées plus haut. Pour un ressortissant
          UE ou suisse, cette localisation ne change rien.
        </p>
        <p>
          La liste exacte des communes concernées figure à l'annexe 1 du règlement du ministère de l'Intérieur du 29 août 2005
          (Dz.U. 2005 nr 188 poz. 1580). Cette liste peut être modifiée : avant tout engagement sur un bien proche d'une frontière
          ou de la mer, vérifiez la commune précise dans la version en vigueur.
        </p>

        <Sub>Ressortissants britanniques : le régime depuis 2021</Sub>
        <p>
          Depuis le 1er janvier 2021, les ressortissants du Royaume-Uni sont traités comme des ressortissants de pays tiers et
          doivent solliciter l'autorisation du ministère, sauf s'ils relèvent de l'accord de retrait, en particulier ceux qui
          résidaient légalement dans un État membre avant la fin de la période de transition et y résident toujours, comme
          l'indique le (
          <a
            href="https://www.gov.pl/web/mswia/uzyskaj-zezwolenie-na-nabycie-nieruchomosci-akcji-udzialow-przez-cudzoziemcow"
            className={EXT}
            {...EXT_PROPS}
          >
            MSWiA
          </a>
          ).
        </p>
      </ContentSection>

      <ContentSection id="autorisation" n={4} title="L'autorisation d'achat immobilier du ministère de l'Intérieur">
        <p>
          Lorsqu'elle est nécessaire, l'autorisation s'obtient sur demande adressée au MSWiA. Il n'existe pas de formulaire imposé
          : la demande reprend les informations prévues par la loi de 1920 et son règlement d'application du 20 juin 2012.
        </p>
        <p>
          Le ministère délivre l'autorisation à une double condition, posée par l'article 1a : l'acquisition ne doit pas menacer la
          défense, la sécurité de l'État ou l'ordre public, et le demandeur doit démontrer des liens avec la Pologne. Ces liens
          peuvent tenir à la nationalité ou à l'origine polonaise, au mariage avec un citoyen polonais, à un titre de séjour, à
          l'exercice d'une activité économique ou agricole sur place, selon le portail public (
          <a href="https://www.biznes.gov.pl/pl/portal/ou209" className={EXT} {...EXT_PROPS}>biznes.gov.pl</a>
          ).
        </p>
        <p>
          Le droit de timbre s'élève à 1 570 zł, versé au Trésor de la ville de Varsovie, et il est remboursable si la procédure se
          termine sans autorisation. La surface acquise pour les besoins de la vie courante est plafonnée à 0,5 hectare pour une
          personne, un hectare pour un couple marié, comme le précise le (
          <a href="https://www.biznes.gov.pl/pl/portal/ou209" className={EXT} {...EXT_PROPS}>MSWiA via biznes.gov.pl</a>
          ).
        </p>
        <p>
          L'instruction relève des affaires particulièrement complexes. Le délai légal indicatif est de deux mois, après recueil de
          l'avis du ministre de la Défense et de l'Agence de sécurité intérieure, auxquels s'ajoute le ministre de l'Agriculture
          pour les terres agricoles. En pratique, la procédure prend souvent deux à quatre mois, parfois davantage.
        </p>
        <p>
          L'autorisation porte sur un bien et un acquéreur déterminés, et reste valable deux ans à compter de sa délivrance,
          indique (
          <a href="https://www.biznes.gov.pl/pl/portal/ou209" className={EXT} {...EXT_PROPS}>biznes.gov.pl</a>
          ). Si l'acte notarié de transfert de propriété n'est pas signé dans ce délai, la décision devient caduque et la
          procédure doit être reprise. Lorsque l'achat n'est pas encore mûr, par exemple parce qu'une division parcellaire est en
          cours, il est possible de demander d'abord une promesse d'autorisation (<PL>promesa</PL>), valable un an, qui sécurise
          l'engagement sans encore permettre l'acquisition.
        </p>
        <p>L'autorisation doit être obtenue avant la signature de l'acte notarié, faute de quoi la vente ne peut être finalisée.</p>
      </ContentSection>

      <ContentSection id="frais-taxes" n={5} title="Frais et taxes pour acheter un bien immobilier en Pologne">
        <p>
          Une fois la question de l'éligibilité réglée, le déroulé et les coûts d'une transaction sont les mêmes pour un étranger
          que pour un Polonais. Les valeurs ci-dessous valent pour 2026 et méritent une vérification annuelle.
        </p>
        <p>
          Avant tout engagement, la vérification du livre foncier (<PL>księga wieczystej</PL>) est l'étape de sécurité essentielle :
          ce registre, consultable en ligne, établit l'identité du propriétaire, la superficie et les éventuelles charges grevant
          le bien, comme une hypothèque. La vente se conclut obligatoirement devant notaire, par acte notarié.
        </p>
        <p>
          La fiscalité dépend du marché. Sur le marché secondaire (un bien d'occasion vendu par un particulier), l'acheteur
          acquitte la taxe sur les actes de droit civil, le <PL>PCC</PL>, au taux de 2 % de la valeur de marché du bien, perçue par
          le notaire au moment de l'acte. Depuis 2024, une exonération de ce PCC existe pour l'achat d'un premier logement sur le
          marché secondaire : elle dépend de l'absence de propriété antérieure, sans condition de nationalité. Sur le marché
          primaire (un logement neuf acheté à un promoteur), il n'y a pas de PCC, mais la TVA est déjà incluse dans le prix, au
          taux de 8 % pour un logement jusqu'à 150 m² et de 23 % au-delà. Le fondement et les taux de ces deux taxes relèvent de la
          loi sur le PCC et de la loi sur la TVA, dont les règles sont rappelées sur le portail fiscal public (
          <a href="https://www.podatki.gov.pl" className={EXT} {...EXT_PROPS}>podatki.gov.pl</a>
          ).
        </p>
        <p>
          À ces taxes s'ajoutent les frais d'acte. La taxe notariale (<PL>taksa notarialna</PL>) suit un barème dégressif plafonné
          par un règlement du ministre de la Justice, reste négociable et supporte 23 % de TVA. L'inscription du droit de propriété
          au livre foncier coûte 200 zł, et l'inscription d'une hypothèque, en cas de crédit (<PL>kredyt hipoteczny</PL>), 200 zł
          supplémentaires.
        </p>
        <p>
          Pour situer ces coûts dans un budget complet et comparer les prix entre villes polonaises, voyez la page{" "}
          <Link to={lp("/cout-de-la-vie")} className={INTERNAL_LINK}>Coût de la vie</Link>.
        </p>
      </ContentSection>

      <ContentSection id="faq" n={6} title="Questions fréquentes">
        <dl className="space-y-6" data-testid="page-faq">
          {ACHETER_BIEN_FAQ.map((item, i) => (
            <div key={i} data-testid={`faq-pair-${i + 1}`}>
              <dt className="font-display text-base sm:text-lg font-semibold text-slate-900">{item.q}</dt>
              <dd className="mt-2 text-slate-700 leading-relaxed">{item.a}</dd>
            </div>
          ))}
        </dl>
      </ContentSection>
    </article>
  );
}
